import React, { useMemo, useState } from "react";
import { Button, Select, Spin, Switch, Table } from "antd";
import { LuArrowRight } from "react-icons/lu";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import UserResponseModal from "../../../components/Modals/UserResponseModal";
import InputResponsesModal from "../../../components/Modals/InputResponsesModal";
import ImageDummy from "../../../assets/images/Image (1).png";
import {
  useGetQuestionByIdQuery,
  useGetQuestionResponsesQuery,
} from "../../../store/api/apiSlice";
import { formatQuestionResponses } from "../../../utils";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import { useSelector } from "react-redux";

const QuestionDetails = () => {
  const { questionId } = useParams();
  const { state: locationState } = useLocation();
  const userRole = useSelector(selectCurrentRole);
  const isOrganizationUser = userRole === "organization";

  // Get form data from navigation state
  const formData = locationState?.formData;

  // Check if we should hide responses (for organization users when submit_organization is null)
  const shouldHideResponses =
    isOrganizationUser &&
    (formData?.submit_organization === null ||
      formData?.submit_organization === undefined);

  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();
  const [selectedInput, setSelectedInput] = useState(null);

  const { data: questionData, isLoading: questionLoading } =
    useGetQuestionByIdQuery(questionId);

  const { data: responsesData, isLoading: responsesLoading } =
    useGetQuestionResponsesQuery(questionId);

  // Format responses data
  const formattedResponses = useMemo(() => {
    if (!responsesData?.data) return [];
    return formatQuestionResponses(responsesData.data);
  }, [responsesData]);

  // Calculate stats - return zero values if responses should be hidden (for organization users)
  const stats = useMemo(() => {
    if (shouldHideResponses) {
      return [
        { value: "0", label: "Response Received" },
        { value: 0, label: "Response Ratio", suffix: "%" },
        { value: questionData?.data?.number || 0, label: "Question Number" },
        { value: 0, label: "Completed Responses" },
      ];
    }

    const total = formattedResponses.length;
    const completed = formattedResponses.filter(
      (r) => r.step === questionData?.data?.number
    ).length;
    return [
      { value: String(total), label: "Response Received" },
      {
        value: total ? ((completed / total) * 100).toFixed(1) : 0,
        label: "Response Ratio",
        suffix: "%",
      },
      { value: questionData?.data?.number || 0, label: "Question Number" },
      { value: completed, label: "Completed Responses" },
    ];
  }, [formattedResponses, questionData, shouldHideResponses]);

  // Process input responses with all response data - return empty array if responses should be hidden
  const inputResponses = useMemo(() => {
    if (shouldHideResponses) return [];
    if (!questionData?.data?.response) return [];

    // First, create a map of response ID to response_assignments for easier lookup
    const responseAssignmentsMap = {};
    if (
      questionData?.data?.responses &&
      Array.isArray(questionData.data.responses)
    ) {
      questionData.data.responses.forEach((response) => {
        if (response._id && response.response_assignments) {
          responseAssignmentsMap[response._id] = response.response_assignments;
        }
      });
    }

    return questionData?.data.response.map((input) => {
      // Get ALL responses for this question, not just filtered ones
      // This ensures we show all 13 responses instead of just the ones matching current input
      const allResponses = questionData?.data?.responses || [];

      // Map the responses to the expected format
      const responseData = allResponses.map((r) => {
        try {
          // Use the original response data structure
          let responseValue = { value: "" };
          let userName = null;
          let userEmail = null;

          // Extract response for the specific input if available
          if (r.response) {
            try {
              const parsedResponse = JSON.parse(r.response);
              const responses = parsedResponse.responses || {};

              Object.values(responses).forEach((val) => {
                if (!val || typeof val !== "object") return;
                const text =
                  val.value != null && typeof val.value !== "object"
                    ? String(val.value).trim()
                    : "";
                if (!text) return;

                const normalized = text.toLowerCase();
                if (
                  normalized === "anonymous" ||
                  normalized === "anonymous user" ||
                  normalized === "n/a" ||
                  normalized === "na"
                ) {
                  return;
                }

                if (val.type === "name" && !userName) {
                  userName = text;
                }
                if (val.type === "email" && !userEmail) {
                  userEmail = text;
                }
              });

              // Find the relevant value for this input
              Object.values(responses).forEach((val) => {
                if (val.inputId === input._id) {
                  responseValue = val;
                }
              });
            } catch (e) {
              console.warn("Error parsing response:", e);
            }
          }

          // Get response_assignments from the map
          const responseAssignments = responseAssignmentsMap[r._id] || [];

          return {
            id: r._id,
            name: userName,
            email: userEmail,
            response:
              responseValue.value === undefined ||
              responseValue.value === null
                ? ""
                : responseValue.value,
            response_assignments: responseAssignments,
            date: new Date(r.date_created || Date.now()).toLocaleDateString(),
          };
        } catch (error) {
          console.warn("Error processing response data:", error);
          return {
            id: r._id || `response-${Math.random()}`,
            name: null,
            email: null,
            response: "",
            date: new Date(Date.now()).toLocaleDateString(),
          };
        }
      });

      return {
        type: input.input_type,
        inputType: input?.input_title || "Untitled Input",
        inputTitle: input.description || "",
        options: input.input_options || [],
        responses: allResponses.length, // Show the actual count
        responseData: responseData,
        // Include slider configuration fields
        show_stars: input.show_stars,
        show_emojis: input.show_emojis,
        show_only_emojis: input.show_only_emojis,
        scale_type: input.scale_type,
        slider_config: input.slider_config,
        // Include all other input fields for completeness
        ...input,
      };
    });
  }, [questionData, shouldHideResponses]);

  if (questionLoading || responsesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="">
      <br />
      <br />
      <div className="space-y-6">
        <div className="bg-white rounded-xl border p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {questionData?.data?.title}
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            {questionData?.data?.description}
          </p>

          <div className="border-t mb-6"></div>

          <div className="flex justify-between items-center mb-2 selectSecondary">
            <h2 className="text-md font-semibold m-0">Overview</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {stats.map((item, index) => (
              <div key={index} className="bg-[#F9FAFB] rounded-xl p-4">
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-gray-900">
                      {item.value}
                    </span>
                    {item.suffix && (
                      <span className="text-gray-500">{item.suffix}</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Only show Input Responses section if responses should not be hidden */}
        {!shouldHideResponses && (
          <div className="bg-white rounded-xl">
            <div className="flex justify-between items-center mb-6 selectSecondary">
              <h2 className="text-md font-semibold m-0">Input Responses</h2>
            </div>
            <div className="space-y-2 border rounded-xl">
              {inputResponses.map((input, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between pt-0 px-6 mt-0 border-b transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedInput(input);
                    setResponseModalVisible(true);
                  }}
                >
                  <div className="py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm text-gray-500">Input Type</div>
                      <div className="text-sm font-medium capitalize">
                        {input.type}
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm text-gray-500">Input Title</div>
                        <div className="text-sm font-medium">
                          {input.inputType || "N/A"}
                        </div>
                      </div>
                      {input.dateType && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm text-gray-500">Date Type</div>
                          <div className="text-sm font-medium">
                            {input.dateType}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 border-l pl-5 pt-3">
                    <div className="text-center">
                      <span className="text-2xl font-semibold">
                        {input.responses}
                      </span>
                      <p className="text-sm text-gray-500">Responses</p>
                    </div>
                    <LuArrowRight className="text-gray-400" size={20} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show message if responses are hidden for organization users */}
        {shouldHideResponses && (
          <div className="bg-white rounded-xl border p-6">
            <p className="text-gray-500 text-center">
              Response data is not available for this form.
            </p>
          </div>
        )}

        <InputResponsesModal
          questionId={questionData?.data?._id}
          visible={responseModalVisible}
          onClose={() => setResponseModalVisible(false)}
          inputType={selectedInput?.type}
          inputData={selectedInput}
          organizationId={questionData?.data?.organization}
        />
      </div>
    </div>
  );
};

export default QuestionDetails;
